/**
 * Canonical URL helpers for Gatsby SSR + browser.
 *
 * Must match sitemap form (gatsby-config siteUrl + trailingSlash: "always")
 * and strip the default-locale /en-US prefix used in onRouteUpdate.
 *
 * SITE_CANONICAL_ORIGIN is the single source of truth — gatsby-config imports it
 * for siteMetadata.siteUrl / sitemap resolveSiteUrl.
 */

export const SITE_CANONICAL_ORIGIN = "https://www.xometry.com";

const DEFAULT_LANG_PREFIX = "/en-US";

// Match gatsby-page-utils applyTrailingSlashOption + edge trailing-slash
// redirect (`^([^.?]*[^/])$`): never append a slash to file-like / dotted paths.
const FILE_SUFFIXES = [".html", ".json", ".js", ".map", ".txt", ".xml", ".pdf"];

/**
 * Paths that must not advertise a self-referencing canonical to JS-less
 * crawlers (error pages + Prismic preview). Sitemap already excludes these.
 */
export function shouldEmitCanonical(pathname: string): boolean {
  if (!pathname) return false;
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return !(
    path === "/404" ||
    path.startsWith("/404/") ||
    path.startsWith("/404.") ||
    path === "/50x" ||
    path.startsWith("/50x/") ||
    path.startsWith("/50x.") ||
    path.startsWith("/preview")
  );
}

function looksLikeFile(path: string): boolean {
  if (FILE_SUFFIXES.some((suffix) => path.endsWith(suffix))) {
    return true;
  }
  // Edge trailing-slash regex excludes any path whose last segment contains "."
  const lastSegment = path.split("/").filter(Boolean).pop() ?? "";
  return lastSegment.includes(".");
}

/**
 * Normalize a pathname for use as a canonical path:
 * - return null for falsy input (do not invent a homepage canonical)
 * - strip default-locale /en-US prefix
 * - ensure a trailing slash except for file-like / dotted paths
 */
export function normalizeCanonicalPath(pathname: string): string | null {
  if (!pathname) {
    return null;
  }

  let path = pathname;

  if (
    path === DEFAULT_LANG_PREFIX ||
    path.startsWith(`${DEFAULT_LANG_PREFIX}/`)
  ) {
    path = path.substring(DEFAULT_LANG_PREFIX.length) || "/";
  }

  if (!looksLikeFile(path) && !path.endsWith("/")) {
    path = `${path}/`;
  }

  return path;
}

/**
 * Build an absolute production canonical URL, or null when the path should
 * not emit a canonical (falsy, error, or preview).
 */
export function buildCanonicalUrl(pathname: string): string | null {
  if (!shouldEmitCanonical(pathname)) {
    return null;
  }
  const normalized = normalizeCanonicalPath(pathname);
  if (!normalized) {
    return null;
  }
  return `${SITE_CANONICAL_ORIGIN}${normalized}`;
}
